import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Collapse, Flex, Steps } from "antd";
import { memo, useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import HowToTell from "./HowToTell.component";
import styles from "./tutorial.module.scss";

const VideoPlayer = memo(({ src }: { src: string; alt: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobilePath = useAppSelector((state) => state.appState.isMobilePath);
  const [_isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isMobilePath) {
      return;
    }

    // Only set up Intersection Observer if the browser supports it
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
          if (entry.isIntersecting && videoRef.current) {
            // Don't auto-play on mobile devices
            if (!isMobilePath) {
              setTimeout(() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(() => {
                    // Handle play error silently
                  });
                }
              }, 2000);
            }
          } else if (videoRef.current) {
            videoRef.current.pause();
          }
        },
        { threshold: 0.5 },
      );

      if (videoRef.current) {
        observer.observe(videoRef.current);
      }

      return () => {
        if (videoRef.current) {
          observer.unobserve(videoRef.current);
        }
      };
    }
  }, [isMobilePath]);

  return <video ref={videoRef} src={src} loop muted playsInline controls className={styles[`tutorial__video`]} />;
});

VideoPlayer.displayName = "VideoPlayer";

const Description = memo(
  ({
    description,
    img,
    stepIndex,
    videoUrl,
    isExpanded,
    onToggle,
  }: {
    description: string;
    img?: string;
    stepIndex: number;
    videoUrl?: string;
    isExpanded: boolean;
    onToggle: () => void;
  }) => {
    return (
      <Flex vertical>
        <p className={styles[`tutorial__step-description`]}>{description}</p>
        {img && <img src={img} alt="Capture Session" className={styles[`tutorial__step-image`]} />}
        {videoUrl && (
          <>
            <Button type="link" onClick={onToggle} className={styles[`tutorial__show-more`]}>
              <Flex align="center" gap={8}>
                {isExpanded ? "Show less" : "Show more"}
                {isExpanded ? <UpOutlined /> : <DownOutlined />}
              </Flex>
            </Button>
            {isExpanded && (
              <div className={styles[`tutorial__video-container`]}>
                <VideoPlayer src={videoUrl} alt={`Step ${stepIndex + 1} demonstration`} />
              </div>
            )}
          </>
        )}
      </Flex>
    );
  },
);

Description.displayName = "Description";

const Title = memo(({ title }: { title: string }) => {
  return <p className={styles[`tutorial__step-title`]}>{title}</p>;
});

Title.displayName = "Title";

const Tutorial = () => {
  const component = "tutorial";
  const { isMobilePath } = useAppSelector((state) => state.appState);
  const [expandedSteps, setExpandedSteps] = useState<{
    [key: number]: boolean;
  }>({ 0: true });

  const toggleStep = (stepIndex: number) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepIndex]: !prev[stepIndex],
    }));
  };

  const items = [
    {
      title: <Title title="CREATE NOTE" />,
      description: (
        <Description
          description="Click the Start A Session button and choose how you want to create your note: Live Session, Dictate, Upload, or Describe."
          stepIndex={0}
          videoUrl="/assets/tutorial-step-1.mp4"
          isExpanded={expandedSteps[0]}
          onToggle={() => toggleStep(0)}
        />
      ),
    },
    {
      title: <Title title="Fill in note information" />,
      description: (
        <Description
          description="Fill in the details of the note when the drawer opens."
          stepIndex={1}
          videoUrl="/assets/tutorial-step-2.mp4"
          isExpanded={expandedSteps[1]}
          onToggle={() => toggleStep(1)}
        />
      ),
    },
    {
      title: <Title title="Generate and Review Note" />,
      description: (
        <Description
          description="Wait until the note generates, and then proceed to review the note."
          stepIndex={2}
          videoUrl="/assets/tutorial-step-3.mp4"
          isExpanded={expandedSteps[2]}
          onToggle={() => toggleStep(2)}
        />
      ),
    },
  ];

  return (
    <Flex vertical className={styles[`${component}__container`]}>
      <Collapse
        defaultActiveKey={["1"]}
        className={styles[`${component}__container-tutorial`]}
        items={[
          {
            key: "1",
            label: "How to get started in 3 steps",
            children: (
              <Steps current={-1} direction="vertical" className={styles[`${component}__step`]} items={items} />
            ),
          },
        ]}
      />
      {!isMobilePath && <HowToTell />}
    </Flex>
  );
};

export default Tutorial;
